export const ProcessStatus = {
  Pending: "PENDING",
  Importing: "IMPORTING",
  BatchCreating: "BATCH_CREATING",
  BatchCreated: "BATCH_CREATED",
  Processing: "PROCESSING",
  PartialCompleted: "PARTIAL_COMPLETED",
  Completed: "COMPLETED",
  Failed: "FAILED",
} as const;

export type ProcessStatus = (typeof ProcessStatus)[keyof typeof ProcessStatus];

export const ProcessItemStatus = {
  Pending: "PENDING",
  Created: "CREATED",
  OcrExecution: "OCR_EXECUTION",
  PageDetection: "PAGE_DETECTION",
  DataExtraction: "DATA_EXTRACTION",
  Completed: "COMPLETED",
  Failed: "FAILED",
} as const;

export type ProcessItemStatus =
  (typeof ProcessItemStatus)[keyof typeof ProcessItemStatus];

export const SupportedDownloadFileType = {
  Excel: "excel",
  Json: "json",
} as const;

export type SupportedDownloadFileType =
  (typeof SupportedDownloadFileType)[keyof typeof SupportedDownloadFileType];

export const WebSocketMessageType = {
  Error: "error",
  Ping: "ping",
  Pong: "pong",
  StatusUpdate: "status_update",
  Authentication: "authentication",
  ConnectionEstablished: "connection_established",
  AuthenticationSuccess: "authentication_success",
  AuthenticationFailed: "authentication_failed",
  AuthenticationTimeout: "authentication_timeout",
} as const;

export type WebSocketMessageType =
  (typeof WebSocketMessageType)[keyof typeof WebSocketMessageType];

// ========== NOTIFICATION MODELS ==========
export interface INotificationMessage {
  type: string;
  message?: string;
  status?: ProcessStatus;
  batch_item_id?: string;
  batch_item_status?: string;
  process_id?: string;
  timestamp: string;
}

// ========== RESPONSE MODELS (from API) ==========
export interface IPresignedUrlResponseModel {
  id: string;
  upload_url: string;
  expires_in: number;
}

interface IBoundingBox {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export interface IExtractedItemResponse {
  id: string;
  key: string;
  value: string;
  page: number;
  original_page?: number;
  uom?: string | null;
  conf?: number | null;
  bbox?: IBoundingBox | null;
}

export interface IExtractedNodeReponse {
  id: string;
  display_name: string;
  level: number;
  is_expandable: boolean;
  children: IExtractedNodeReponse[];
  data?: IExtractedItemResponse | null;
}

export interface IExtractedResultModel {
  display_nodes: IExtractedNodeReponse[];
  preview_url: string | null;
  no_pages?: number | null;
  is_pdf?: boolean | null;
}

export interface IDownloadedFileResponseModel {
  file_name: string;
  s3_key: string;
}

// Process Item for individual files within a process
export interface IProcessItemModel {
  id: string;
  file_name: string;
  status: string;
  demo_image: string | null;
  created_at: string;
  error_message: string | null;
}

// Process Response Info for processed results list
export interface IProcessInfoModel {
  id: string;
  status: string;
  created_at: string;
  is_batch: boolean;
  error_message: string | null;
  items: IProcessItemModel[];
  mapping_enabled?: boolean;
  doctype_code?: string | null;
}

// ========== VIEW MODELS (for UI) ==========
export interface IExtractedItemViewModel {
  id: string;
  key: string;
  value: string;
  page: number;
  originalPage: number;
  uom?: string | null;
  bbox?: IBoundingBox | null;
  conf?: number | null;
}

export interface IExtractedNodeViewModel {
  id: string;
  displayName: string;
  level: number;
  isExpandable: boolean;
  children: IExtractedNodeViewModel[];
  data?: IExtractedItemViewModel | null;
}

export interface IExtractedResultViewModel {
  displayNode: IExtractedNodeViewModel[];
  previewUrl: string | null;
  isPdf: boolean;
  noPages: number;
}

// Process Item ViewModel for individual files within a process
export interface IProcessItemViewModel {
  id: string;
  fileName: string;
  status: string;
  demoImage: string | null;
  createdAt: string;
  errorMessage: string | null;
}

// Process Info ViewModel for processed results list
export interface IProcessInfoViewModel {
  id: string;
  status: string;
  createdAt: string;
  isBatch: boolean;
  errorMessage: string | null;
  items: IProcessItemViewModel[];
  /** Per-run mapping opt-in — gates the mapping status / "Open mapping" UI. */
  mappingEnabled: boolean;
  /** Doctype the process ran under; lets a review link reject a foreign process. */
  doctypeCode?: string | null;
}

export interface IDownloadedFileViewModel {
  fileName: string;
  file: Blob;
}

export interface IProcessListResponse {
  items: IProcessInfoModel[];
}

export interface IProcessedResultsFilterViewModel {
  startDate?: string | null;
  endDate?: string | null;
  fileName?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  processId?: string | null;
  limit?: number | null;
}

// ========== USER INSTRUCTION MODELS ==========
export interface UserInstructionResponse {
  id: string;
  name: string;
  instruction: string;
  doctype_code: string;
  created_by: string;
}

export interface UserInstructionViewModel {
  id: string;
  name: string;
  instruction: string;
  doctypeCode: string;
  createdBy: string;
}
